import type { ReactNode } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { sysColors } from "@/assets/theme/colors";
import { ChartData } from "./Chart";
import ChartTooltip from "./ChartTooltip";

import classNames from "classnames";
import ChartSkeleton from "@/components/Skeleton/ChartSkeleton";
import { formatDisplayCurrency } from "@/lib/currencyHelpers";
import ChartLegend from "./ChartLegend";

const COLORS = [
  sysColors["sys-brand-primary"],
  sysColors["sys-brand-secondary"],
  sysColors["sys-brand-tertiary"],
  sysColors["sys-brand-primary-container"]
];

export type PieChartProps = {
  // Chart data
  data: ChartData;
  colors?: string[];

  // Content
  title: string;
  totalValue?: number;
  /** When false, the headline total and tooltip show raw counts instead of currency. Default true. */
  formatAsCurrency?: boolean;
  /** When false, omits the large total under the title (useful when KPIs already show the count). */
  showTotal?: boolean;
  emptyLabel?: ReactNode;

  // State
  loading?: boolean;

  // Layout
  className?: string;
  height?: number;
  /**
   * When false, removes the card chrome (`border`, `rounded-lg`, and `p-6`) so the chart can sit
   * inside a parent card that owns padding and border. Default keeps the self-contained card look.
   */
  bordered?: boolean;
};

const PieChartComponent = (props: PieChartProps) => {
  const {
    data,
    title,
    colors = COLORS,
    totalValue,
    formatAsCurrency = true,
    showTotal = true,
    emptyLabel,
    loading = false,
    className,
    height = 225,
    bordered = true
  } = props;
  const chartData = data.map((item) => ({ ...item, label: item.value }));
  const displayTotal = totalValue ?? data.reduce((acc, item) => acc + item.value, 0);
  const formattedTotal = formatAsCurrency ? `$${formatDisplayCurrency(displayTotal)}` : String(displayTotal);
  const isEmpty = !loading && data.length === 0;

  return (
    <div
      className={classNames(
        "flex flex-col items-start w-full h-full",
        bordered && "border border-sys-brand-outline rounded-lg p-6",
        className
      )}
      data-testid="pie-chart"
    >
      <div className={classNames("w-full", !showTotal && "mb-6")}>
        <p className="text-title-medium text-sys-brand-on-surface">{title}</p>
        {showTotal && displayTotal > 0 ? (
          <div className="text-display-small text-sys-brand-on-surface">{formattedTotal}</div>
        ) : null}
      </div>

      <div className="w-full">
        {loading ? (
          <ChartSkeleton type="pie" />
        ) : isEmpty && emptyLabel ? (
          <div className="flex w-full items-center justify-center" style={{ height }}>
            {typeof emptyLabel === "string" ? (
              <p className="m-0 text-center text-body-small text-sys-brand-on-surface-variant">{emptyLabel}</p>
            ) : (
              emptyLabel
            )}
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={height}>
              <PieChart>
                <Pie data={chartData} dataKey="value" cx="50%" cy="50%" outerRadius={90} fill="#8884d8">
                  {chartData.map((_entry, index) => (
                    <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active = false, payload }) => {
                    const items =
                      payload?.map((entry) => ({
                        name: String(entry.payload?.name ?? entry.name ?? ""),
                        value: Number(entry.value ?? 0),
                        color: String(entry.payload?.fill ?? entry.color ?? "")
                      })) ?? [];

                    return (
                      <ChartTooltip active={active} title={title} items={items} formatAsCurrency={formatAsCurrency} />
                    );
                  }}
                />
              </PieChart>
            </ResponsiveContainer>

            <ChartLegend
              items={chartData.map((entry, index) => ({
                name: entry.name,
                color: colors[index % colors.length],
                visible: true
              }))}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default PieChartComponent;
